import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { ComicHeading, ComicButton } from '../components/ComicPanel';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Loader2, X, Maximize2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Reader = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [item, setItem] = useState(null);
  const [navigation, setNavigation] = useState({ previous: null, next: null });
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  
  // Track if we've loaded initial progress
  const initialProgressLoaded = useRef(false);
  const progressSaveTimeout = useRef(null);

  useEffect(() => {
    fetchContent();
    return () => {
      // Clear any pending progress save on unmount
      if (progressSaveTimeout.current) {
        clearTimeout(progressSaveTimeout.current);
      }
    };
  }, [slug, token]);

  // Save reading progress when page changes (debounced)
  useEffect(() => {
    if (!item || !token || !initialProgressLoaded.current) return;
    
    // Clear previous timeout
    if (progressSaveTimeout.current) {
      clearTimeout(progressSaveTimeout.current);
    }
    
    // Debounce save to avoid too many requests
    progressSaveTimeout.current = setTimeout(() => {
      saveProgress(currentPage);
    }, 1000);
    
    return () => {
      if (progressSaveTimeout.current) {
        clearTimeout(progressSaveTimeout.current);
      }
    };
  }, [currentPage, item, token]);

  const saveProgress = async (pageIndex) => {
    if (!item || !token) return;
    
    try {
      await axios.put(`${API}/progress/`, {
        contentItemId: item.id,
        lastViewedImageIndex: pageIndex
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Silently fail - progress saving is not critical
      console.debug('Failed to save progress:', error);
    }
  };

  const fetchContent = async () => {
    setLoading(true);
    setError(null);
    initialProgressLoaded.current = false;
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [itemResponse, navResponse] = await Promise.all([
        axios.get(`${API}/content/item/${slug}`, { headers }),
        axios.get(`${API}/content/navigation/${slug.split('/')[0] || 'skits'}/${slug}`, { headers }).catch(() => ({ data: {} }))
      ]);
      
      setItem(itemResponse.data);
      setNavigation(navResponse.data);
      
      // Check for page param in URL first
      const pageParam = searchParams.get('page');
      if (pageParam !== null) {
        const pageIndex = parseInt(pageParam, 10);
        if (!isNaN(pageIndex) && pageIndex >= 0 && pageIndex < itemResponse.data.imagePaths.length) {
          setCurrentPage(pageIndex);
          initialProgressLoaded.current = true;
          return;
        }
      }
      
      // Otherwise, fetch saved progress
      if (token) {
        try {
          const progressResponse = await axios.get(`${API}/progress/${itemResponse.data.id}`, { headers });
          if (progressResponse.data && progressResponse.data.lastViewedImageIndex !== undefined) {
            const savedPage = progressResponse.data.lastViewedImageIndex;
            if (savedPage >= 0 && savedPage < itemResponse.data.imagePaths.length) {
              setCurrentPage(savedPage);
            }
          }
        } catch (err) {
          // No saved progress, start from beginning
          setCurrentPage(0);
        }
      } else {
        setCurrentPage(0);
      }
      
      initialProgressLoaded.current = true;
    } catch (err) {
      console.error('Failed to fetch content:', err);
      if (err.response?.status === 403) {
        setError('This content requires a verified account.');
      } else if (err.response?.status === 404) {
        setError('Content not found.');
      } else {
        setError('Failed to load content. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = useCallback((e) => {
    if (!item) return;
    
    if (e.key === 'ArrowLeft') {
      setCurrentPage(prev => Math.max(0, prev - 1));
    } else if (e.key === 'ArrowRight') {
      setCurrentPage(prev => Math.min(item.imagePaths.length - 1, prev + 1));
    } else if (e.key === 'Escape' && fullscreen) {
      setFullscreen(false);
    }
  }, [item, fullscreen]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const goToPrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const goToNextPage = () => {
    if (item && currentPage < item.imagePaths.length - 1) {
      setCurrentPage(prev => prev + 1);
    }
  };

  // Touch handling
  const [touchStart, setTouchStart] = useState(null);
  
  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        goToNextPage();
      } else {
        goToPrevPage();
      }
    }
    
    setTouchStart(null);
  };

  if (loading) {
    return (
      <Layout hideFooter>
        <div className="min-h-screen flex items-center justify-center bg-slate-900">
          <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout hideFooter>
        <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4">
          <div className="text-center">
            <p className="text-red-500 text-lg mb-4">{error}</p>
            <ComicButton variant="primary" onClick={() => navigate(-1)}>
              Go Back
            </ComicButton>
          </div>
        </div>
      </Layout>
    );
  }

  if (!item) return null;

  const hasVideo = !!item.videoUrl;
  const totalPages = item.imagePaths?.length || 0;
  const currentImage = item.imagePaths?.[currentPage];

  if (fullscreen) {
    return (
      <div 
        className="fixed inset-0 bg-black z-50 flex items-center justify-center"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <button
          onClick={() => setFullscreen(false)}
          className="absolute top-4 right-4 text-white/50 hover:text-white z-10"
          data-testid="reader-exit-fullscreen"
        >
          <X className="w-8 h-8" />
        </button>
        
        <img
          src={currentImage}
          alt={`Page ${currentPage + 1}`}
          className="max-w-full max-h-full object-contain"
          loading="lazy"
        />
        
        {/* Navigation zones */}
        <button
          onClick={goToPrevPage}
          className="absolute left-0 top-0 bottom-0 w-1/3 cursor-pointer"
          disabled={currentPage === 0}
        />
        <button
          onClick={goToNextPage}
          className="absolute right-0 top-0 bottom-0 w-1/3 cursor-pointer"
          disabled={currentPage === totalPages - 1}
        />
        
        {/* Page indicator */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/50 text-sm">
          {currentPage + 1} / {totalPages}
        </div>
      </div>
    );
  }

  return (
    <Layout hideFooter>
      <div className="min-h-screen bg-slate-900">
        {/* Header */}
        <div className="bg-slate-800 border-b-2 border-slate-700 py-4">
          <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
            <Link 
              to={`/library/${item.section}`}
              className="inline-flex items-center gap-2 text-slate-400 hover:text-yellow-500 transition-colors"
              data-testid="reader-back"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to {item.section}</span>
            </Link>
            
            <ComicHeading as="h1" className="text-lg sm:text-xl truncate max-w-xs sm:max-w-md" data-testid="reader-title">
              {item.title}
            </ComicHeading>
            
            <button
              onClick={() => setFullscreen(true)}
              className="text-slate-400 hover:text-yellow-500 transition-colors"
              data-testid="reader-fullscreen"
            >
              <Maximize2 className="w-5 h-5" />
            </button>
          </div>
        </div>

       {/* Reader Area */}
<div 
  className="flex-1 flex flex-col items-center justify-center py-8 px-4"
  onTouchStart={handleTouchStart}
  onTouchEnd={handleTouchEnd}
>
  {hasVideo ? (
    <div className="w-full max-w-4xl bg-slate-800 overflow-hidden border-2 border-slate-700">
      {item.videoType === 'mp4' ? (
        <video
          src={item.videoUrl}
          controls
          playsInline
          className="w-full max-h-[80vh] bg-black"
          poster={item.imagePaths?.[0] || undefined}
          data-testid="reader-video"
        />
      ) : (
        <div className="p-8 text-center">
          {item.imagePaths?.[0] && (
            <img
              src={item.imagePaths[0]}
              alt={item.title}
              className="w-full max-w-md mx-auto mb-6"
            />
          )}
          <p className="text-slate-300 mb-6">
            This Sporc Minute opens as an external video.
          </p>
          <a
            href={item.videoUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-block bg-yellow-500 text-black font-black uppercase px-6 py-3"
            data-testid="reader-video-link"
          >
            Watch Video
          </a>
        </div>
      )}
    </div>
  ) : (
    <>
      <div className="relative w-full max-w-4xl aspect-[3/4] bg-slate-800 overflow-hidden">
        <img
          src={currentImage}
          alt={`Page ${currentPage + 1}`}
          className="w-full h-full object-contain"
          loading="lazy"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://placehold.co/800x1200/1e293b/F59E0B?text=Page+Not+Found';
          }}
          data-testid="reader-image"
        />

        <button
          onClick={goToPrevPage}
          disabled={currentPage === 0}
          className="absolute left-2 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 text-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
          data-testid="reader-prev-page"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>

        <button
          onClick={goToNextPage}
          disabled={currentPage === totalPages - 1}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 text-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
          data-testid="reader-next-page"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      <div className="mt-4 text-slate-400">
        Page {currentPage + 1} of {totalPages}
      </div>
    </>
  )}

          {/* Caption */}
          {item.caption && (
            <p className="mt-4 text-slate-300 text-center max-w-2xl">{item.caption}</p>
          )}

          {/* Item navigation */}
          <div className="mt-8 flex items-center gap-4">
            {navigation.previous && (
              <Link to={`/read/${navigation.previous.slug}`}>
                <ComicButton variant="ghost" className="flex items-center gap-2" data-testid="reader-prev-item">
                  <ArrowLeft className="w-4 h-4" />
                  Previous
                </ComicButton>
              </Link>
            )}
            {navigation.next && (
              <Link to={`/read/${navigation.next.slug}`}>
                <ComicButton variant="ghost" className="flex items-center gap-2" data-testid="reader-next-item">
                  Next
                  <ArrowRight className="w-4 h-4" />
                </ComicButton>
              </Link>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Reader;
